import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { from, fromEvent, Subject, timer } from 'rxjs';
import { debounceTime, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import has from 'lodash-es/has';
import get from 'lodash-es/get';

import { IPicture } from '@finder/shared/components/picture/picture.interface';
import { ListCardOptions } from '@finder/features/list/components/list-card/list-card.interface';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { LIST_CARD_ANIMATIONS } from './list-card.animations';
import { AuthenticationService } from '@finder/shared/services/authentication/authentication.service';
import { DiningListReservationsService } from '@finder/features/list/services/dining-list-reservations/dining-list-reservations.service';
import { IDiningListReservation } from '@finder/features/list/services/dining-list-reservations/dining-list-reservations.interface';
import { SessionStorage } from '@finder/shared/services/session-storage/session-storage.service';
/* eslint-disable max-len */
import { FIND_DINE_LIST_AVAILABILITY } from '@finder/features/list/components/filters/components/dine-availability-filter/dine-availability-filter.constants';
import { LIST_CARD_ANCHOR_AS_EXTERNAL } from '@finder/shared/constants/app.constants';
import { AnchorComponent } from '@finder/shared/components/anchor/anchor.component';
import { FontIconComponent } from '@finder/shared/components/font-icon/font-icon.component';
import { SectionComponent } from '@finder/shared/components/section/section.component';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { OperatingStatusComponent } from '@finder/shared/components/operating-status/operating-status.component';
import { ButtonComponent } from '@finder/shared/components/button/button.component';
import { PremierAccessComponent } from '@finder/shared/components/premier-access/premier-access.component';
import { PictureComponent } from '@finder/shared/components/picture/picture.component';
import { LoadingSpinnerComponent } from '@finder/shared/components/loading-spinner/loading-spinner.component';
import { CommonModule } from '@angular/common';
@Component({
    selector: 'finder-list-card',
    templateUrl: './list-card.component.html',
    styleUrls: ['./list-card.component.scss'],
    animations: [...LIST_CARD_ANIMATIONS],
    standalone: true,
    imports: [
        AnchorComponent,
        ButtonComponent,
        CommonModule,
        FontIconComponent,
        LoadingSpinnerComponent,
        OperatingStatusComponent,
        PictureComponent,
        PremierAccessComponent,
        SafeHtmlPipe,
        SectionComponent,
    ]
})
export class ListCardComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() card: any;
    @Input() labels: any;
    @Input() debug = false;
    @Input() options: ListCardOptions;
    @Input() disablePills = false;
    @Output() flyToMarker = new EventEmitter<any>();
    @Output() validating = new EventEmitter<boolean>();
    @ViewChild('cardElement', { static: false, read: ElementRef }) cardElement: ElementRef;

    facetsLabel: string;
    isDesktop: boolean;
    picture: IPicture;
    parkNames: Array<string>;
    type: any;
    isEEC: boolean;
    clipboardCopied = false;
    reservation: IDiningListReservation;
    internalLink: boolean;
    waitTime = '';

    multipleLocationsConfig: any;

    private destroy$ = new Subject<void>();

    constructor(
        private viewport: ViewportDetectionService,
        private windowRef: WindowRef,
        private sessionStorage: SessionStorage,
        private diningListReservations: DiningListReservationsService,
        private authenticationService: AuthenticationService
    ) { }

    ngOnInit() {
        this.isDesktop = this.viewport.isDesktop();
        this.isEEC = has(this.card, 'facets.eec-category');

        this.reservation = this.diningListReservations.getClosestReservation(
            this.card.facilityId,
            get(this.card, 'restaurantsIds', []),
            this.card.availabilitySearchDate
        );
        const cardUrl = get(this.card, 'url', '');

        this.internalLink =
            cardUrl.startsWith('/') && !LIST_CARD_ANCHOR_AS_EXTERNAL.find(url => url === cardUrl.replaceAll('/', ''));

        this.checkMultipleDineLocations();
        this.getCardImage();
        this.getTypeReadback();
    }

    ngAfterViewInit() {
        fromEvent(this.cardElement.nativeElement.closest('.list-card-container'), 'keydown')
            .pipe(
                filter((event: KeyboardEvent) => this.isDesktop && event.key.toLocaleLowerCase() === 'm'),
                takeUntil(this.destroy$)
            )
            .subscribe((event: KeyboardEvent) => {
                this.requestFlyToMarker(event);
            });

        this.viewport.onViewportResize()
            .pipe(
                debounceTime(100),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.isDesktop = this.viewport.isDesktop());
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    clipboard(event, text) {
        event.preventDefault();
        event.stopPropagation();

        from(this.windowRef.nativeWindow.navigator.clipboard.writeText(text))
            .pipe(
                switchMap(() => {
                    this.clipboardCopied = true;

                    return timer(1000);
                })
            )
            .subscribe(() => this.clipboardCopied = false);
    }

    redirectToBookingUrl(event, offer) {
        event.preventDefault();
        event.stopPropagation();
        // setup spinner so the user knows which offer is being validated
        offer.validating = true;

        const { date, time, productType } = offer;
        const location = this.windowRef.nativeWindow.location;
        const parsedUrl = new URL(`${location.origin}${offer.url}`);
        parsedUrl.searchParams.append('offerOrigin', location.pathname);

        this.validating.emit(true);

        // set this key before the login validation in case the user is redirected to login page,
        // that way the availability search is triggered immediately when the user comes back
        this.sessionStorage.set(FIND_DINE_LIST_AVAILABILITY, true);

        this.authenticationService.validateLogin()
            .pipe(
                switchMap((swid) => {
                    // if the login validation is successful we no longer need the session key
                    this.sessionStorage.remove(FIND_DINE_LIST_AVAILABILITY);

                    return this.diningListReservations.checkConflict(
                        swid,
                        date,
                        time,
                        productType,
                        parsedUrl
                    );
                }),
                take(1)
            )
            .subscribe({
                next: (conflictValidated) => {
                    if (!conflictValidated.redirect) {
                        offer.validating = false;
                        this.validating.emit(false);

                        return;
                    }

                    this.windowRef.nativeWindow.location.href = conflictValidated.url;
                },
                error: (error) => {
                    offer.validating = false;
                    this.validating.emit(false);
                }
            });
    }

    redirectToEECUrl(event) {
        event.preventDefault();
        event.stopPropagation();

        const location = this.windowRef.nativeWindow.location;
        const path = `/enchanting-extras-collection/${this.card.urlFriendlyId}/`;
        const parsedUrl = new URL(`${location.origin}${path}`).toString();

        this.authenticationService.validateLogin(true, {
            returnUrl: parsedUrl
        })
        .pipe(
            take(1)
        )
        .subscribe(() => {
            this.windowRef.nativeWindow.location.href = parsedUrl;
        });
    }

    redirectToTicketsUrl(event, url) {
        event.preventDefault();
        event.stopPropagation();

        const location = this.windowRef.nativeWindow.location;
        const parsedUrl = new URL(`${location.origin}${url}`);

        this.windowRef.nativeWindow.location.href = parsedUrl.toString();
    }

    requestFlyToMarker(event) {
        // Stop the event from bubbling
        event.preventDefault();
        event.stopPropagation();

        if (!this.card.marker) {
            return;
        }

        // Check for filteredMarkers. Loop through them on each click
        if (this.card.filteredMarkers.markers.length) {
            // If there is no element at the counter then reset it
            if (!this.card.filteredMarkers.markers[this.card.filteredMarkers.counter]) {
                this.card.filteredMarkers.counter = 0;
            }

            this.flyToMarker.emit(this.card.filteredMarkers.markers[this.card.filteredMarkers.counter++]);

            return;
        }

        this.flyToMarker.emit(this.card.marker);
    }

    stopClickPropagation(event) {
        // Open link of the nested anchor
        event.stopPropagation();
    }

    private checkMultipleDineLocations() {
        if (!this.card.multipleLocations) {
            return;
        }

        this.multipleLocationsConfig = {
            expanded: this.card.hasAvailability,
            labels: {
                sectionTitle: this.card.multipleLocations.title
            }
        };
    }

    private getCardImage() {
        const image = get(this.card, 'media.finderStandardThumb');
        if (image) {
            this.picture = {
                alt: image.alt,
                mobile: image.url
            };
        }
    }

    private getTypeReadback() {
        if (!this.card.type) {
            return;
        }

        this.type = {
            readback: get(this.card, 'type.facets'),
            icon: get(this.card, 'type.icon')
        };
    }
}
